import z from "zod";

import {
  audienceUserSchema,
  type AudienceUser,
  jobStatusEnum,
  type JobStatus,
} from "../Audiences/types";

export { audienceUserSchema as listUserSchema, type AudienceUser as ListUser };
export { jobStatusEnum, type JobStatus };

export const listStatsSchema = z.object({
  userCount: z.coerce.number(),
  newContactsThisMonth: z.coerce.number(),
});

export type ListStats = z.infer<typeof listStatsSchema>;

export const listSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  editable: z.boolean(),
  phonePrimary: z.boolean(),
  fields: z.array(z.string()),
  stats: listStatsSchema.nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type List = z.infer<typeof listSchema>;

export const getListResponseSchema = z.object({
  list: listSchema,
  stats: listStatsSchema,
});

export type GetListResponse = z.infer<typeof getListResponseSchema>;

export const listCreateSchema = listSchema.omit({
  id: true,
  stats: true,
  createdAt: true,
  updatedAt: true,
});

export type ListCreate = z.infer<typeof listCreateSchema>;

export const listUpdateSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  editable: z.boolean().optional(),
  fields: z.array(z.string()).optional(),
});

export type ListUpdate = z.infer<typeof listUpdateSchema>;

export const paginationResponseSchema = z.object({
  nextCursor: z.string(),
  totalCount: z.coerce.number(),
});

export type PaginationResponse = z.infer<typeof paginationResponseSchema>;

export const paginatedListsResponseSchema = z.object({
  lists: z.array(listSchema),
  pagination: paginationResponseSchema,
});

export type PaginatedListsResponse = z.infer<
  typeof paginatedListsResponseSchema
>;

export const paginatedUsersResponseSchema = z.object({
  users: z.array(audienceUserSchema),
  pagination: paginationResponseSchema,
});

export type PaginatedUsersResponse = z.infer<
  typeof paginatedUsersResponseSchema
>;

export const listsSearchSchema = z.object({
  search: z.string().optional(),
  email: z.string().optional(),
  page: z.coerce.number().int().gte(1).optional(),
  pageSize: z.coerce.number().int().gte(10).lte(100).optional(),
  ordering: z.enum(["createdAt", "updatedAt", "name"]).optional(),
  sortOrder: z.enum(["asc", "desc"]).optional(),
});

export type ListsSearch = z.infer<typeof listsSearchSchema>;

export const listContactsSearchSchema = z.object({
  search: z.string().optional(),
  page: z.coerce.number().int().gte(1).optional(),
  pageSize: z.coerce.number().int().gte(10).lte(100).optional(),
  ordering: z
    .enum(["createdAt", "updatedAt", "email", "phone", "firstName", "lastName"])
    .optional(),
  sortOrder: z.enum(["asc", "desc"]).optional(),
});

export type ListContactsSearch = z.infer<typeof listContactsSearchSchema>;

export const exportResponseSchema = z.object({
  jobId: z.string().uuid(),
});

export type ExportResponse = z.infer<typeof exportResponseSchema>;

export const exportJobStatusSchema = z.object({
  job: z.object({
    id: z.string(),
    siteId: z.string(),
    listId: z.string().optional(),
    audienceId: z.string().optional(),
    type: z.literal("JOB_TYPE_EXPORT"),
    status: jobStatusEnum,
    result: z
      .object({
        totalRows: z.coerce.number(),
        processedRows: z.coerce.number(),
        successfulRows: z.coerce.number(),
        failedRows: z.coerce.number(),
        skippedRows: z.coerce.number(),
        errors: z.array(z.string()),
        fileUrl: z.string(),
      })
      .nullish(),
    createdAt: z.string(),
    updatedAt: z.string(),
  }),
});

export type ExportJobStatus = z.infer<typeof exportJobStatusSchema>;

export const importResponseSchema = z
  .object({
    job_id: z.string().uuid(),
    invalid_rows: z.array(z.string()).optional().default([]),
  })
  .transform((data) => ({
    jobId: data.job_id,
    invalidRows: data.invalid_rows,
  }));

export type ImportResponse = z.infer<typeof importResponseSchema>;

const previewStatusSchema = z.enum([
  "subscribed",
  "unsubscribed",
  "not_subscribed",
]);

const mediumPreviewSchema = z
  .object({
    to_subscribed: z.number(),
    to_unsubscribed: z.number(),
    unchanged: z.number(),
  })
  .transform((data) => ({
    toSubscribed: data.to_subscribed,
    toUnsubscribed: data.to_unsubscribed,
    unchanged: data.unchanged,
  }));

// A new contact has no before-state, which the backend sends as "".
const previewBeforeSchema = previewStatusSchema.or(z.literal(""));

const previewSampleSchema = z
  .object({
    email: z.string(),
    phone: z.string(),
    existing: z.boolean(),
    email_before: previewBeforeSchema,
    email_after: previewStatusSchema,
    sms_before: previewBeforeSchema,
    sms_after: previewStatusSchema,
  })
  .transform((data) => ({
    email: data.email,
    phone: data.phone,
    existing: data.existing,
    emailBefore: data.email_before,
    emailAfter: data.email_after,
    smsBefore: data.sms_before,
    smsAfter: data.sms_after,
  }));

// Mirrors audiences' ImportPreviewResponse. Every count is of distinct contacts.
export const importPreviewSchema = z
  .object({
    valid_rows: z.number(),
    scanned_rows: z.number(),
    complete: z.boolean(),
    skipped_rows: z.number(),
    new_contacts: z.number(),
    existing_contacts: z.number(),
    consent_changed_contacts: z.number(),
    to_subscribed_contacts: z.number(),
    email: mediumPreviewSchema,
    sms: mediumPreviewSchema,
    // .default() would leave a null as null
    samples: z.array(previewSampleSchema).nullish(),
  })
  .transform((data) => ({
    validRows: data.valid_rows,
    scannedRows: data.scanned_rows,
    complete: data.complete,
    skippedRows: data.skipped_rows,
    newContacts: data.new_contacts,
    existingContacts: data.existing_contacts,
    consentChangedContacts: data.consent_changed_contacts,
    toSubscribedContacts: data.to_subscribed_contacts,
    email: data.email,
    sms: data.sms,
    samples: data.samples ?? [],
  }));

export type ImportPreview = z.infer<typeof importPreviewSchema>;
export type ImportPreviewResponse = z.input<typeof importPreviewSchema>;
export type ImportPreviewSample = ImportPreview["samples"][number];

export const jobResultSchema = z.object({
  totalRows: z.coerce.number(),
  processedRows: z.coerce.number(),
  successfulRows: z.coerce.number(),
  failedRows: z.coerce.number(),
  skippedRows: z.coerce.number(),
  errors: z.array(z.string()),
});

export const listImportJobSchema = z.object({
  job: z.object({
    id: z.string(),
    siteId: z.string(),
    listId: z.string(),
    type: z.literal("JOB_TYPE_IMPORT"),
    status: jobStatusEnum,
    result: jobResultSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
  }),
});

export type ListImportJob = z.infer<typeof listImportJobSchema>;

export const sourceEnum = z.enum([
  "MANUAL",
  "LIST_CSV_IMPORT",
  "API_IMPORT",
  "SHOPIFY",
]);

export type Source = z.infer<typeof sourceEnum>;

export const batchAddUsersResponseSchema = z.object({
  success: z.array(z.string()),
  failure: z.array(z.string()),
});

export type BatchAddUsersResponse = z.infer<typeof batchAddUsersResponseSchema>;

export const deleteUsersResponseSchema = z.object({
  userIds: z.array(z.string()),
});

export type DeleteUsersResponse = z.infer<typeof deleteUsersResponseSchema>;

export const userListsResponseSchema = z.object({
  lists: z.array(
    z.object({
      listId: z.string(),
      name: z.string(),
      addedAt: z.string(),
      memberCount: z.string(),
    }),
  ),
});

export type UserListsResponse = z.infer<typeof userListsResponseSchema>;

export type UserListsLookup =
  | { kind: "email"; value: string }
  | { kind: "phone"; value: string }
  | { kind: "userId"; value: string };
